import { useState } from 'react';
import { NetflixRow, NetflixCard } from './NetflixRow';
import { CardDetailModal } from './CardDetailModal';

export const PublicationsRow = () => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const publications = [
    {
      title: "NIST SP 800-100: Information Security for Managers",
      subtitle: "Contributor • Federal Standard",
      description: "Comprehensive framework for information security management in government and enterprise environments, providing strategic guidance for executive leadership. This framework has shaped how thousands of organizations approach security governance, establishing industry best practices for risk-based security management across federal agencies nationwide.",
      match: "Federal Standard",
      tags: ["NIST", "Federal Standard", "Security Management"],
      imageSrc: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800",
      impact: "Adopted by 1000+ federal agencies and Fortune 500 companies worldwide",
      link: "https://csrc.nist.gov/pubs/sp/800/100/upd1/final"
    },
    {
      title: "NIST SP 800-55: Security Measurement Strategy",
      subtitle: "Contributor • Federal Standard",
      description: "Systematic approach to measuring and improving information security program effectiveness through quantitative metrics and performance indicators. Revolutionized how organizations measure security effectiveness, enabling data-driven security investments and ROI calculations across both government and private sector organizations.",
      match: "Federal Standard",
      tags: ["NIST", "Security Metrics", "Performance Measurement"],
      imageSrc: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800",
      impact: "Global standard for security ROI and effectiveness measurement",
      link: "https://csrc.nist.gov/pubs/sp/800/55/v1/final"
    },
    {
      title: "CSA CCM v4.0: Cloud Controls Matrix",
      subtitle: "Lead Author • Global Cloud Standard",
      description: "Cloud security controls matrix defining shared responsibility models and implementation guidelines for secure cloud computing environments. Critical framework used by Fortune 500 companies and cloud providers worldwide to establish secure cloud operations and regulatory compliance, defining the global standard for cloud security controls.",
      match: "Global Standard",
      tags: ["CSA", "Cloud Security", "Global Standard"],
      imageSrc: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800",
      impact: "Used by 500+ cloud providers and 10,000+ enterprise customers globally",
      link: "https://cloudsecurityalliance.org/artifacts/cloud-controls-matrix-v4/"
    },
    {
      title: "CSA CCM v4.0 Implementation Guidelines",
      subtitle: "Lead Author • Implementation Standard",
      description: "Comprehensive implementation guidelines for Cloud Controls Matrix v4.0, providing detailed guidance on operationalizing cloud security controls in enterprise environments. Essential resource for organizations implementing secure cloud architectures and achieving regulatory compliance in multi-cloud environments.",
      match: "Implementation Guide",
      tags: ["CSA", "Implementation", "Cloud Controls"],
      imageSrc: "https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?w=800",
      impact: "Definitive guide for enterprise cloud security implementation",
      link: "https://cloudsecurityalliance.org/artifacts/ccm-v4-0-implementation-guidelines"
    },
    {
      title: "AI Risk and Governance Framework",
      subtitle: "Author • Wharton School",
      description: "Comprehensive analysis of AI governance frameworks, risk management strategies, and regulatory compliance approaches for enterprise AI deployment. Pioneering work in AI governance that bridges technical implementation with business strategy and regulatory compliance, referenced in AI policy development across multiple countries.",
      match: "Academic Research",
      tags: ["AI Governance", "Risk Management", "Academic Research"],
      imageSrc: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800",
      impact: "Referenced in EU AI Act development and enterprise AI policies worldwide",
      link: "https://ai-analytics.wharton.upenn.edu/industry/finance/artificial-intelligence-risk-governance/"
    },
    {
      title: "Forbes: Cloud Observability Challenges at Scale",
      subtitle: "Author • Industry Publication",
      description: "Strategic analysis of cloud observability challenges in enterprise environments and practical solutions for achieving visibility at scale. Covers advanced monitoring strategies, automated threat detection, and governance frameworks for complex multi-cloud architectures serving millions of users.",
      match: "Industry Insight",
      tags: ["Cloud Observability", "Enterprise Scale", "Forbes"],
      imageSrc: "https://images.unsplash.com/photo-1504868584819-f8e8b4b6d7e3?w=800",
      impact: "Thought leadership on enterprise cloud monitoring and governance",
      link: "https://www.forbes.com/councils/forbestechcouncil/2025/10/10/cloud-observability-challenges-at-scale-and-how-to-solve-them/"
    }
  ];

  const leadershipRoles = [
    {
      title: "Senior Fellow, Policy and Applied AI",
      subtitle: "The Digital Economist",
      description: "Leading AI policy development and governance frameworks at the intersection of technology, economics, and regulation. Influencing national AI policy through research and thought leadership, contributing to policy discussions on AI safety, ethics, and economic impact across government and industry stakeholders.",
      match: "Policy Leadership",
      tags: ["AI Policy", "Government Relations", "Thought Leadership"],
      imageSrc: "https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=800"
    },
    {
      title: "Global Chair - Cloud Controls Matrix (CCM)",
      subtitle: "Cloud Security Alliance",
      description: "Leading global development of cloud security standards and best practices for enterprise cloud adoption. Defining cloud security standards used by thousands of organizations worldwide, coordinating international working groups, and establishing the definitive framework for cloud security controls and compliance.",
      match: "Global Standards",
      tags: ["Cloud Security", "Global Standards", "Industry Leadership"],
      imageSrc: "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=800"
    },
    {
      title: "Global Chair - AI Controls Framework",
      subtitle: "Cloud Security Alliance",
      description: "Pioneering AI security and governance controls for responsible AI deployment in enterprise environments. Creating the foundational framework for AI security governance, establishing industry-first AI-specific security controls, and coordinating global expert consensus on AI risk management approaches.",
      match: "AI Innovation",
      tags: ["AI Security", "Framework Development", "Global Coordination"],
      imageSrc: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=800"
    }
  ];

  const handleCardClick = (item, type) => {
    setSelectedCard({...item, type});
    setModalOpen(true);
  };

  return (
    <>
      <NetflixRow title="Publications & Industry Standards" id="publications">
        {publications.map((pub, index) => (
          <NetflixCard
            key={index}
            title={pub.title}
            subtitle={pub.subtitle}
            description={pub.description}
            match={pub.match}
            tags={pub.tags}
            imageSrc={pub.imageSrc}
            onClick={() => handleCardClick(pub, 'publication')}
          />
        ))}
      </NetflixRow>
      
      <NetflixRow title="Global Leadership Positions">
        {leadershipRoles.map((role, index) => (
          <NetflixCard
            key={index}
            title={role.title}
            subtitle={role.subtitle}
            description={role.description}
            match={role.match}
            tags={role.tags}
            imageSrc={role.imageSrc}
            onClick={() => handleCardClick(role, 'leadership')}
          />
        ))}
      </NetflixRow>
      
      <CardDetailModal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        card={selectedCard}
        type="publication"
      />
    </>
  );
};